/**
 * Onglet Documents justificatifs - ÉTAPE E1 + E2
 * Upload avec drag & drop, liste groupée par catégorie, viewer inline
 * + Versioning, Soft delete avec annulation, Alertes expiration
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Progress } from "../../ui/progress";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../ui/collapsible";
import { 
  Upload, FileText, Image, File, Plus, Eye, EyeOff, Download, Trash2, 
  ChevronDown, ChevronRight, X, Loader2, AlertCircle, FolderOpen,
  RefreshCw, History, Clock, AlertTriangle, Hash
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import { 
  DOCUMENT_CATEGORY_OPTIONS,
  getDocumentCategoryLabel,
  getDocumentCategoryColor
} from "../../../lib/hr-constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Max file size: 10 MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed file types
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif"
];

// File type display info
const FILE_TYPE_INFO = {
  "application/pdf": { icon: FileText, color: "text-red-500", label: "PDF" },
  "image/jpeg": { icon: Image, color: "text-blue-500", label: "JPG" },
  "image/png": { icon: Image, color: "text-green-500", label: "PNG" },
  "image/heic": { icon: Image, color: "text-purple-500", label: "HEIC" },
  "image/heif": { icon: Image, color: "text-purple-500", label: "HEIF" },
};

// Initial upload form state
const INITIAL_UPLOAD_FORM = {
  category: "",
  custom_label: "",
  issue_date: "",
  expiry_date: "",
  document_number: "",
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// Check if file is valid
const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `Le fichier dépasse la taille maximale de 10 Mo` };
  }
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: `Type de fichier non autorisé. Formats acceptés : PDF, JPG, PNG, HEIC` };
  }
  return { valid: true };
};

// Upload file item component
const UploadFileItem = ({ file, form, onChange, onRemove, isUploading, progress }) => {
  const fileInfo = FILE_TYPE_INFO[file.type] || { icon: File, color: "text-slate-500", label: "?" };
  const FileIcon = fileInfo.icon;
  const requiresCustomLabel = form.category === "other";
  
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* File info */}
          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <FileIcon className={`w-6 h-6 ${fileInfo.color}`} />
          </div>
          
          <div className="flex-1 min-w-0 space-y-4">
            {/* File name & size */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
              </div>
              {!isUploading && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 shrink-0"
                  onClick={onRemove}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Progress bar during upload */}
            {isUploading && (
              <div className="space-y-1">
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-slate-500">{progress}%</p>
              </div>
            )}
            
            {/* Form fields */}
            {!isUploading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Category (required) */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Catégorie *</Label>
                  <Select 
                    value={form.category} 
                    onValueChange={(v) => onChange("category", v)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_CATEGORY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Custom label (if category = other) */}
                {requiresCustomLabel && (
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Libellé personnalisé *</Label>
                    <Input
                      value={form.custom_label}
                      onChange={(e) => onChange("custom_label", e.target.value)}
                      placeholder="Ex: Attestation employeur"
                      className="h-9"
                    />
                  </div>
                )}
                
                {/* Issue date (optional) */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Date d'émission</Label>
                  <DatePickerFr
                    value={form.issue_date}
                    onChange={(v) => onChange("issue_date", v)}
                    placeholder="JJ/MM/AAAA"
                  />
                </div>
                
                {/* Expiry date (optional) */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Date d'expiration</Label>
                  <DatePickerFr
                    value={form.expiry_date}
                    onChange={(v) => onChange("expiry_date", v)}
                    placeholder="JJ/MM/AAAA"
                  />
                </div>
                
                {/* Document number (optional) */}
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs text-slate-600">Numéro de document</Label>
                  <Input
                    value={form.document_number}
                    onChange={(e) => onChange("document_number", e.target.value)}
                    placeholder="Ex: 123456789"
                    className="h-9"
                  />
                  {(form.category === "cni" || form.category === "passport") && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Ce numéro sera chiffré côté serveur
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Document list item with versioning, enhanced expiration alerts, and document number reveal
const DocumentItem = ({ doc, onView, onDownload, onDelete, onReplace, onShowHistory, onRevealDocNumber, allDocuments }) => {
  const fileInfo = FILE_TYPE_INFO[doc.mime_type] || { icon: File, color: "text-slate-500", label: "?" };
  const FileIcon = fileInfo.icon;
  const categoryColor = getDocumentCategoryColor(doc.category);
  
  // State for document number visibility
  const [docNumberVisible, setDocNumberVisible] = useState(false);
  
  // Auto-hide document number after 30 seconds
  useEffect(() => {
    let timer;
    if (docNumberVisible && doc.document_number) {
      timer = setTimeout(() => {
        setDocNumberVisible(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [docNumberVisible, doc.document_number]);
  
  // Handle document number reveal
  const handleRevealDocNumber = () => {
    const newVisible = !docNumberVisible;
    setDocNumberVisible(newVisible);
    if (newVisible && onRevealDocNumber) {
      onRevealDocNumber(doc);
    }
  };
  
  // Check if document has a sensitive number (CNI, passport, residence permit)
  const hasSensitiveNumber = doc.document_number && 
    ['cni', 'id_card', 'passport', 'residence_permit'].includes(doc.category);
  
  // Check expiration status with enhanced alerts
  const getExpirationStatus = () => {
    if (!doc.expiry_date) return null;
    const expiry = new Date(doc.expiry_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { 
        label: "Expiré", 
        color: "bg-red-100 text-red-700 border-red-200",
        icon: AlertTriangle,
        urgent: true
      };
    }
    if (daysUntilExpiry <= 30) {
      return { 
        label: `Expire dans ${daysUntilExpiry}j`, 
        color: "bg-red-100 text-red-700 border-red-200",
        icon: AlertTriangle,
        urgent: true
      };
    }
    if (daysUntilExpiry <= 60) {
      return { 
        label: `Expire dans ${daysUntilExpiry}j`, 
        color: "bg-orange-100 text-orange-700 border-orange-200",
        icon: Clock,
        urgent: false
      };
    }
    return null;
  };
  
  const expirationStatus = getExpirationStatus();
  
  // Check if document has previous versions
  const hasVersionHistory = doc.version > 1 || allDocuments?.some(d => 
    d.previous_version_id === doc.id || doc.previous_version_id
  );
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors group ${
      expirationStatus?.urgent 
        ? 'border-red-200 bg-red-50/50 hover:bg-red-50' 
        : 'border-slate-200 hover:bg-slate-50'
    }`}>
      {/* File icon */}
      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
        <FileIcon className={`w-5 h-5 ${fileInfo.color}`} />
      </div>
      
      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-slate-900 truncate">
            {doc.custom_label || doc.original_filename}
          </p>
          <Badge variant="outline" className={`${categoryColor} text-xs shrink-0`}>
            {getDocumentCategoryLabel(doc.category)}
          </Badge>
          {doc.version > 1 && (
            <Badge variant="secondary" className="text-xs shrink-0 bg-slate-100">
              v{doc.version}
            </Badge>
          )}
          {expirationStatus && (
            <Badge variant="outline" className={`${expirationStatus.color} text-xs shrink-0 flex items-center gap-1`}>
              {expirationStatus.icon && <expirationStatus.icon className="w-3 h-3" />}
              {expirationStatus.label}
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Ajouté le {formatDateFr(doc.uploaded_at || doc.created_at)}
          {doc.expiry_date && ` • Expire le ${formatDateFr(doc.expiry_date)}`}
        </p>
        
        {/* Document number display with eye icon for sensitive documents */}
        {hasSensitiveNumber && (
          <div className="flex items-center gap-2 mt-1">
            <Hash className="w-3 h-3 text-slate-400" />
            <span className="text-xs font-mono text-slate-600">
              {docNumberVisible ? doc.document_number : "••••••••••••"}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 text-slate-500 hover:text-blue-600"
                    onClick={handleRevealDocNumber}
                    data-testid={`toggle-doc-number-${doc.id}`}
                  >
                    {docNumberVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{docNumberVisible ? "Masquer" : "Afficher"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {docNumberVisible && (
              <span className="text-xs text-slate-400">(masqué dans 30s)</span>
            )}
          </div>
        )}
      </div>
      
      {/* Actions - visibles sur mobile, hover sur desktop */}
      <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => onView(doc)}
          title="Visualiser"
          data-testid={`view-doc-${doc.id}`}
        >
          <Eye className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => onDownload(doc)}
          title="Télécharger"
          data-testid={`download-doc-${doc.id}`}
        >
          <Download className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 hidden sm:flex"
          onClick={() => onReplace(doc)}
          title="Remplacer"
          data-testid={`replace-doc-${doc.id}`}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
        {hasVersionHistory && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 hidden sm:flex"
            onClick={() => onShowHistory(doc)}
            title="Historique des versions"
            data-testid={`history-doc-${doc.id}`}
          >
            <History className="w-4 h-4" />
          </Button>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onDelete(doc)}
          title="Supprimer"
          data-testid={`delete-doc-${doc.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Document category group
const DocumentCategoryGroup = ({ category, documents, allDocuments, onView, onDownload, onDelete, onReplace, onShowHistory, onRevealDocNumber }) => {
  const [isOpen, setIsOpen] = useState(true);
  const categoryLabel = getDocumentCategoryLabel(category);
  
  // Count expiring documents
  const expiringCount = documents.filter(doc => {
    if (!doc.expiry_date) return false;
    const expiry = new Date(doc.expiry_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 60;
  }).length;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto hover:bg-slate-100">
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="font-medium text-slate-700">{categoryLabel}</span>
            <Badge variant="secondary" className="text-xs">{documents.length}</Badge>
            {expiringCount > 0 && (
              <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 border-orange-200">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {expiringCount} à renouveler
              </Badge>
            )}
          </div>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pl-6 mt-2">
        {documents.map(doc => (
          <DocumentItem 
            key={doc.id} 
            doc={doc}
            allDocuments={allDocuments}
            onView={onView}
            onDownload={onDownload}
            onDelete={onDelete}
            onReplace={onReplace}
            onShowHistory={onShowHistory}
            onRevealDocNumber={onRevealDocNumber}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Document viewer modal
const DocumentViewer = ({ document, open, onClose, token }) => {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!open || !document) return;
    
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use file_id for download endpoint
        const response = await axios.get(
          `${API}/hr/files/${document.file_id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        setBlobUrl(url);
      } catch (err) {
        console.error("Error fetching document:", err);
        setError("Impossible de charger le document");
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocument();
    
    // Cleanup is handled separately to avoid dependency issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document, token]);
  
  // Cleanup blob URL when component unmounts or document changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);
  
  const handleDownload = () => {
    if (blobUrl && document) {
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.original_filename;
      link.click();
    }
  };
  
  const isPdf = document?.mime_type === "application/pdf";
  const isImage = document?.mime_type?.startsWith("image/");
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span className="truncate">{document?.original_filename}</span>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 overflow-auto" style={{ maxHeight: "70vh" }}>
          {loading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
          
          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <AlertCircle className="w-12 h-12 mb-2" />
              <p>{error}</p>
            </div>
          )}
          
          {!loading && !error && blobUrl && (
            <>
              {isPdf && (
                <iframe
                  src={blobUrl}
                  className="w-full h-[65vh] rounded-lg border"
                  title={document.original_filename}
                />
              )}
              {isImage && (
                <img
                  src={blobUrl}
                  alt={document.original_filename}
                  className="max-w-full max-h-[65vh] mx-auto rounded-lg"
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Empty state
const EmptyState = ({ onUpload }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <FolderOpen className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun document</h3>
    <p className="text-sm text-slate-500 mb-4 max-w-sm">
      Ajoutez des documents justificatifs pour ce salarié : CNI, passeport, RIB, diplômes...
    </p>
    <Button onClick={onUpload} className="bg-blue-600 hover:bg-blue-700">
      <Plus className="w-4 h-4 mr-2" />
      Ajouter le premier document
    </Button>
  </div>
);

export default function DocumentsTab({ employee, onUpdate }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload state
  const [isUploadMode, setIsUploadMode] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [uploadForms, setUploadForms] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  
  // Viewer state
  const [viewerDoc, setViewerDoc] = useState(null);
  
  // Delete confirmation state (E2)
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lastDeletedDoc, setLastDeletedDoc] = useState(null);
  
  // Replace mode state (E2 - Versioning)
  const [replaceDoc, setReplaceDoc] = useState(null);
  const replaceInputRef = useRef(null);
  
  // Version history state (E2)
  const [historyDoc, setHistoryDoc] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/hr/employees/${employee.id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { documents: [...], total: N }
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError("Impossible de charger les documents");
    } finally {
      setLoading(false);
    }
  }, [employee.id, token]);
  
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);
  
  // Group documents by category (only current and not deleted)
  const documentsByCategory = useMemo(() => {
    const grouped = {};
    documents
      .filter(doc => doc.is_current && !doc.is_deleted)
      .forEach(doc => {
        if (!grouped[doc.category]) {
          grouped[doc.category] = [];
        }
        grouped[doc.category].push(doc);
      });
    return grouped;
  }, [documents]);
  
  // Count of visible documents
  const visibleDocCount = useMemo(() => {
    return documents.filter(doc => doc.is_current && !doc.is_deleted).length;
  }, [documents]);
  
  // Handle file selection
  const handleFileSelect = (files) => {
    const newFiles = [];
    const newForms = { ...uploadForms };
    
    Array.from(files).forEach(file => {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error("Fichier invalide", { description: `${file.name}: ${validation.error}` });
        return;
      }
      
      const fileId = `${file.name}-${Date.now()}`;
      newFiles.push({ id: fileId, file });
      newForms[fileId] = { ...INITIAL_UPLOAD_FORM };
    });
    
    if (newFiles.length > 0) {
      setFilesToUpload(prev => [...prev, ...newFiles]);
      setUploadForms(newForms);
      setIsUploadMode(true);
    }
  };
  
  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };
  
  // Handle form change for a file
  const handleFormChange = (fileId, field, value) => {
    setUploadForms(prev => ({
      ...prev,
      [fileId]: { ...prev[fileId], [field]: value }
    }));
  };
  
  // Remove file from upload queue
  const removeFile = (fileId) => {
    setFilesToUpload(prev => prev.filter(f => f.id !== fileId));
    setUploadForms(prev => {
      const newForms = { ...prev };
      delete newForms[fileId];
      return newForms;
    });
    
    if (filesToUpload.length === 1) {
      setIsUploadMode(false);
    }
  };
  
  // Cancel upload mode
  const cancelUpload = () => {
    setFilesToUpload([]);
    setUploadForms({});
    setIsUploadMode(false);
    setUploadProgress({});
  };
  
  // Validate all forms
  const validateForms = () => {
    for (const { id } of filesToUpload) {
      const form = uploadForms[id];
      if (!form.category) {
        toast.error("Formulaire incomplet", { description: "Veuillez sélectionner une catégorie pour chaque document" });
        return false;
      }
      if (form.category === "other" && !form.custom_label?.trim()) {
        toast.error("Formulaire incomplet", { description: "Veuillez saisir un libellé personnalisé pour les documents 'Autre'" });
        return false;
      }
    }
    return true;
  };
  
  // Upload all files
  const uploadFiles = async () => {
    if (!validateForms()) return;
    
    setIsUploading(true);
    let successCount = 0;
    
    for (const { id, file } of filesToUpload) {
      const form = uploadForms[id];
      
      try {
        setUploadProgress(prev => ({ ...prev, [id]: 0 }));
        
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", form.category);
        if (form.custom_label) formData.append("custom_label", form.custom_label);
        if (form.issue_date) formData.append("issue_date", form.issue_date);
        if (form.expiry_date) formData.append("expiry_date", form.expiry_date);
        if (form.document_number) formData.append("document_number", form.document_number);
        
        await axios.post(
          `${API}/hr/employees/${employee.id}/documents`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [id]: percent }));
            }
          }
        );
        
        successCount++;
        setUploadProgress(prev => ({ ...prev, [id]: 100 }));
        
      } catch (err) {
        console.error("Error uploading file:", err);
        toast.error("Erreur d'upload", { 
          description: `${file.name}: ${err.response?.data?.detail || "Erreur inconnue"}` 
        });
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} document(s) ajouté(s)`);
      await fetchDocuments();
      onUpdate?.();
    }
    
    cancelUpload();
  };
  
  // View document
  const handleView = (doc) => {
    setViewerDoc(doc);
  };
  
  // Download document
  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/hr/files/${doc.file_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.original_filename;
      link.click();
      URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error("Error downloading document:", err);
      toast.error("Erreur", { description: "Impossible de télécharger le document" });
    }
  };
  
  // Delete document - show confirmation modal (E2)
  const handleDeleteClick = (doc) => {
    setDeleteDoc(doc);
  };
  
  // Confirm delete (soft delete with undo toast)
  const confirmDelete = async () => {
    if (!deleteDoc) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/hr/employees/${employee.id}/documents/${deleteDoc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const deletedDocCopy = { ...deleteDoc };
      setLastDeletedDoc(deletedDocCopy);
      setDeleteDoc(null);
      
      await fetchDocuments();
      onUpdate?.();
      
      // Show toast with undo action
      toast.success("Document supprimé", {
        description: "Ce document sera définitivement supprimé dans 30 jours",
        action: {
          label: "Annuler",
          onClick: () => handleUndoDelete(deletedDocCopy)
        },
        duration: 8000
      });
      
    } catch (err) {
      console.error("Error deleting document:", err);
      toast.error("Erreur", { description: "Impossible de supprimer le document" });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Undo delete (restore document)
  const handleUndoDelete = async (doc) => {
    try {
      await axios.post(`${API}/hr/documents/${doc.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Document restauré", {
        description: `"${doc.original_filename}" a été restauré`
      });
      
      await fetchDocuments();
      onUpdate?.();
      
    } catch (err) {
      console.error("Error restoring document:", err);
      toast.error("Erreur", { description: "Impossible de restaurer le document" });
    }
  };
  
  // Replace document - trigger file input (E2 - Versioning)
  const handleReplaceClick = (doc) => {
    setReplaceDoc(doc);
    // Trigger file selection
    setTimeout(() => replaceInputRef.current?.click(), 100);
  };
  
  // Handle replacement file selection
  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceDoc) {
      setReplaceDoc(null);
      return;
    }
    
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error("Fichier invalide", { description: validation.error });
      setReplaceDoc(null);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", replaceDoc.category);
      if (replaceDoc.custom_label) formData.append("custom_label", replaceDoc.custom_label);
      if (replaceDoc.issue_date) formData.append("issue_date", replaceDoc.issue_date);
      if (replaceDoc.expiry_date) formData.append("expiry_date", replaceDoc.expiry_date);
      
      await axios.put(
        `${API}/hr/employees/${employee.id}/documents/${replaceDoc.id}/replace`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      toast.success("Document remplacé", {
        description: `Nouvelle version (v${(replaceDoc.version || 1) + 1}) créée`
      });
      
      await fetchDocuments();
      onUpdate?.();
      
    } catch (err) {
      console.error("Error replacing document:", err);
      toast.error("Erreur", { description: "Impossible de remplacer le document" });
    } finally {
      setReplaceDoc(null);
      if (replaceInputRef.current) {
        replaceInputRef.current.value = '';
      }
    }
  };
  
  // Show version history (E2)
  const handleShowHistory = async (doc) => {
    setHistoryDoc(doc);
    setLoadingHistory(true);
    
    try {
      const response = await axios.get(
        `${API}/hr/employees/${employee.id}/documents/${doc.id}/versions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVersionHistory(response.data.versions || []);
    } catch (err) {
      console.error("Error fetching version history:", err);
      // If no versions endpoint, show single version
      setVersionHistory([doc]);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Log document number reveal to audit trail
  const handleRevealDocNumber = useCallback(async (doc) => {
    try {
      await axios.post(`${API}/hr/audit/reveal`, {
        employee_id: employee.id,
        field: "document_number",
        resource_type: "document",
        resource_id: doc.id,
        details: { 
          category: doc.category,
          document_label: doc.custom_label || doc.original_filename
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(`[AUDIT] Document number revealed for document ${doc.id}`);
    } catch (err) {
      // Silent fail - audit logging should not block UI
      console.error("Error logging document number reveal:", err);
    }
  }, [employee.id, token]);
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p>{error}</p>
        <Button variant="outline" onClick={fetchDocuments} className="mt-4">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload zone - Simplifié sur mobile */}
      <Card 
        className={`border-2 border-dashed transition-colors ${
          isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-200 hover:border-slate-300'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col items-center justify-center text-center">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-3 ${
              isDragging ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <Upload className={`w-5 h-5 sm:w-6 sm:h-6 ${isDragging ? 'text-blue-600' : 'text-slate-400'}`} />
            </div>
            {/* Desktop: Drag & Drop */}
            <p className="hidden sm:block text-sm font-medium text-slate-700 mb-1">
              Glissez-déposez des fichiers ici
            </p>
            <p className="hidden sm:block text-xs text-slate-500 mb-3">
              ou
            </p>
            {/* Mobile: Simplifié */}
            <p className="sm:hidden text-sm font-medium text-slate-700 mb-3">
              Ajouter un document
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-btn"
              className="w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Sélectionner des fichiers
            </Button>
            <p className="text-xs text-slate-400 mt-3">
              PDF, JPG, PNG, HEIC • Max 10 Mo
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Files to upload */}
      {isUploadMode && filesToUpload.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="font-semibold text-slate-900">
              {filesToUpload.length} fichier(s) à uploader
            </h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={cancelUpload} disabled={isUploading} className="flex-1 sm:flex-none">
                Annuler
              </Button>
              <Button 
                onClick={uploadFiles} 
                disabled={isUploading}
                className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
                data-testid="confirm-upload-btn"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span className="hidden sm:inline">Upload en cours...</span>
                    <span className="sm:hidden">Upload...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Uploader tout</span>
                    <span className="sm:hidden">Uploader</span>
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="space-y-3">
            {filesToUpload.map(({ id, file }) => (
              <UploadFileItem
                key={id}
                file={file}
                form={uploadForms[id] || INITIAL_UPLOAD_FORM}
                onChange={(field, value) => handleFormChange(id, field, value)}
                onRemove={() => removeFile(id)}
                isUploading={isUploading}
                progress={uploadProgress[id] || 0}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Documents list */}
      {!isUploadMode && (
        <>
          {visibleDocCount === 0 ? (
            <EmptyState onUpload={() => fileInputRef.current?.click()} />
          ) : (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Documents ({visibleDocCount})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-4">
                    {Object.entries(documentsByCategory).map(([category, docs]) => (
                      <DocumentCategoryGroup
                        key={category}
                        category={category}
                        documents={docs}
                        allDocuments={documents}
                        onView={handleView}
                        onDownload={handleDownload}
                        onDelete={handleDeleteClick}
                        onReplace={handleReplaceClick}
                        onShowHistory={handleShowHistory}
                        onRevealDocNumber={handleRevealDocNumber}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Hidden input for file replacement */}
      <input
        ref={replaceInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
        onChange={handleReplaceFile}
        className="hidden"
      />
      
      {/* Document viewer modal */}
      <DocumentViewer
        document={viewerDoc}
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        token={token}
      />
      
      {/* Delete confirmation modal (E2) */}
      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent className="sm:max-w-md w-[calc(100%-2rem)] mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Le document <strong>"{deleteDoc?.original_filename}"</strong> sera déplacé 
                dans la corbeille.
              </p>
              <p className="text-amber-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Il sera définitivement supprimé dans 30 jours.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={isDeleting} className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Version history modal (E2) */}
      <Dialog open={!!historyDoc} onOpenChange={() => setHistoryDoc(null)}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique des versions
            </DialogTitle>
            <DialogDescription>
              {historyDoc?.original_filename}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : versionHistory.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Aucun historique disponible
              </p>
            ) : (
              <div className="space-y-3">
                {versionHistory.map((version, index) => {
                  const fileInfo = FILE_TYPE_INFO[version.mime_type] || { icon: File, color: "text-slate-500" };
                  const FileIcon = fileInfo.icon;
                  const isCurrent = version.is_current;
                  
                  return (
                    <div 
                      key={version.id || index}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        isCurrent ? 'border-blue-200 bg-blue-50' : 'border-slate-200'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <FileIcon className={`w-5 h-5 ${fileInfo.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900">
                            Version {version.version || 1}
                          </p>
                          {isCurrent && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              Actuelle
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {formatDateFr(version.uploaded_at || version.created_at)}
                          {version.original_filename && ` • ${version.original_filename}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => {
                            setHistoryDoc(null);
                            setViewerDoc(version);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => handleDownload(version)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setHistoryDoc(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
